import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, PlusIcon, PencilIcon, PaperClipIcon, CalendarDaysIcon, UserIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { ticketsAPI, referenceAPI } from '../../api/api';

const ProjectOwnerTicketModal = ({ isOpen, onClose, onCreateTicket, selectedProject }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority_id: null,
    assigned_to_user_id: null,
    created_by_user_id: null,
    status_id: null,
    project: selectedProject?.id || null,
    due_date: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [referenceData, setReferenceData] = useState({
    priorities: [],
    users: [],
    statuses: []
  });
  const [loading, setLoading] = useState(false);

  // Modal açıkken scroll kilitle
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Modal açıldığında verileri yükle
  useEffect(() => {
    if (isOpen && selectedProject?.id) {
      loadReferenceData();
      setFormData(prev => ({
        ...prev,
        project: selectedProject.id,
        created_by_user_id: user?.id || null
      }));
    }
  }, [isOpen, selectedProject?.id, user]);

  const loadReferenceData = async () => {
    try {
      setLoading(true);

      // Priorities yükle
      const prioritiesRes = await referenceAPI.getPriorities();

      // Proje kullanıcılarını yükle
      const usersRes = await referenceAPI.getUsersByProjectDepartment(selectedProject.id);

      // Proje status'larını yükle
      const statusRes = await ticketsAPI.getStatusesByProject(selectedProject.id);

      setReferenceData({
        priorities: prioritiesRes.success ? prioritiesRes.data : [],
        users: usersRes.success ? usersRes.data : [],
        statuses: statusRes.success ? statusRes.data : []
      });

      // Default değerleri set et
      if (statusRes.success && statusRes.data?.length > 0) {
        const toDoStatus = statusRes.data.find(s =>
          s.status_name?.toLowerCase().includes('to do') ||
          s.status_name?.toLowerCase().includes('beklemede')
        );
        if (toDoStatus) {
          setFormData(prev => ({ ...prev, status_id: toDoStatus.id }));
        }
      }

      // Default reporter olarak kullanıcıyı set et (zaten useEffect'te set ediliyor)
    } catch (error) {
      // Silently handle error
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const ticketData = {
        title: formData.title,
        description: formData.description,
        project: formData.project,
        status_id: formData.status_id,
        priority_id: formData.priority_id,
        assigned_to_user_id: formData.assigned_to_user_id || null,
        created_by_user_id: formData.created_by_user_id || user?.id || null,
        due_date: formData.due_date || null
      };

      const response = await ticketsAPI.createTicket(ticketData);

      if (response.success) {
        const ticketData = response.data || response;
        onCreateTicket(ticketData);

        // Reset form
        setFormData({
          title: '',
          description: '',
          priority_id: null,
          assigned_to_user_id: null,
          created_by_user_id: user?.id || null,
          status_id: null,
          project: selectedProject?.id || null,
          due_date: ''
        });

        onClose();
      } else {
        alert(response.message || 'Ticket oluşturulurken hata oluştu!');
      }
    } catch (error) {
      // Silently handle error
      alert('Ticket oluşturulurken hata oluştu!');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4 outline-none focus:outline-none overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-2xl transform transition-all animate-in fade-in zoom-in duration-300">
        {/* Header */}
        <div className="border-b border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <span className="text-2xl">📄</span>
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h2 className="font-dynamic-title font-semibold text-gray-900 dark:text-white">Yeni Ticket Oluştur</h2>
                  <button
                    className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                </div>

                <div className="flex items-center space-x-4 text-sm">
                  <span className="text-gray-500 dark:text-gray-400">Proje: {selectedProject?.project_name}</span>
                  <span className="px-2 py-1 rounded-full text-xs font-medium border bg-gray-100 text-gray-800 border-gray-300">
                    {selectedProject?.project_code}
                  </span>
                </div>
              </div>
            </div>

            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Content */}
              <div className="lg:col-span-2 space-y-6">
                {loading && (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="text-gray-600 dark:text-gray-400 mt-2">Veriler yükleniyor...</p>
                  </div>
                )}

                {/* Title */}
                <div>
                  <h3 className="font-dynamic-heading font-medium text-gray-900 dark:text-white mb-3">Ticket Başlığı</h3>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      required
                      className="w-full bg-transparent text-gray-700 dark:text-gray-300 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none"
                      placeholder="Ticket başlığını girin..."
                    />
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h3 className="font-dynamic-heading font-medium text-gray-900 dark:text-white mb-3">Açıklama</h3>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full bg-transparent text-gray-700 dark:text-gray-300 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none resize-none"
                      placeholder="Sorunu veya talebi detaylı olarak açıklayın... (Opsiyonel)"
                    />
                  </div>
                </div>

                {/* Attachments - Placeholder for now */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-3">Attachments</h3>
                  <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                    <PaperClipIcon className="w-8 h-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" />
                    <p className="text-sm text-gray-500 dark:text-gray-400">Dosya ekleme özelliği yakında...</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      Şimdilik CreateTicketModal üzerinden dosya ekleyebilirsiniz
                    </p>
                  </div>
                </div>
              </div>

              {/* Sidebar */}
              <div className="space-y-6">
                {/* Priority */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Priority</h4>
                  <select
                    name="priority_id"
                    value={formData.priority_id || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Priority seçin...</option>
                    {(referenceData.priorities || []).map((priority) => (
                      <option key={priority.id} value={priority.id}>
                        {priority.priority_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Status */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Status</h4>
                  <select
                    name="status_id"
                    value={formData.status_id || ''}
                    onChange={handleInputChange}
                    required
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Status seçin...</option>
                    {(referenceData.statuses || []).map((status) => (
                      <option key={status.id} value={status.id}>
                        {status.status_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Assignee */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Assignee</h4>
                  <select
                    name="assigned_to_user_id"
                    value={formData.assigned_to_user_id || ''}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Unassigned</option>
                    {(referenceData.users || []).map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.full_name || `${user.first_name} ${user.last_name}`}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Reporter */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Reporter</h4>
                  <select
                    name="created_by_user_id"
                    value={formData.created_by_user_id || ''}
                    onChange={handleInputChange}
                    className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select reporter...</option>
                    {(referenceData.users || []).map((userItem) => (
                      <option key={userItem.id} value={userItem.id}>
                        {userItem.full_name || `${userItem.first_name} ${userItem.last_name}`}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Due Date */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Due Date</h4>
                  <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                    <CalendarDaysIcon className="w-4 h-4" />
                    <input
                      type="date"
                      name="due_date"
                      value={formData.due_date}
                      onChange={handleInputChange}
                      className="border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    />
                  </div>
                </div>

                {/* Labels */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Labels</h4>
                  <div className="flex flex-wrap gap-1">
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">new</span>
                    <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">project-owner</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 bg-gray-50 dark:bg-gray-700">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button type="button" className="text-sm text-blue-600 hover:text-blue-700">
                Quick Templates
              </button>
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md hover:bg-gray-50 dark:hover:bg-gray-500"
              >
                Close
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={!formData.title.trim() || !formData.priority_id || !formData.status_id || isSubmitting || loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Creating...' : 'Create Ticket'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProjectOwnerTicketModal;
